import { Injectable } from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';
import { ConfirmationModalComponent } from '../../ui/confirmation-modal/confirmation-modal.component';
import { PlatformSwitcherService } from './platform-switcher.service';
import {
  PlatformSwitcherRouteKey,
  PlatformSwitcherRouteMatch,
} from './platform-switcher-route.map';

/**
 * Shared "switch to Phoenix" behavior for any page that hosts the switcher
 * button (the main header, the create/edit-survey page). Each host provides
 * its own instance (see `providers` on the component) and just feeds it
 * `switcherMatch`/`isFormDirty` from whatever route/dirty-tracking mechanism
 * it already has, then delegates `canSwitchUi`/`switchToPhoenix()` to it.
 */
@Injectable()
export class PlatformSwitcherPageController {
  public switcherMatch: PlatformSwitcherRouteMatch | null = null;
  public isFormDirty = false;

  constructor(
    private _platformSwitcher: PlatformSwitcherService,
    private _modal: ModalService
  ) {}

  setSwitcherMatchFromUrl(url: string): void {
    this.switcherMatch = this._platformSwitcher.matchCurrentRoute(url);
  }

  get canSwitchUi(): boolean {
    return !!this.switcherMatch && this._platformSwitcher.canSwitch();
  }

  switchToPhoenix(): void {
    if (!this.switcherMatch) {
      return;
    }
    const { routeKey, params } = this.switcherMatch;

    if (!this.isFormDirty) {
      this._doSwitchToPhoenix(routeKey, params);
      return;
    }

    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: 'You have unsaved changes. Switching to the new experience now will discard them.',
        cancelButtonLabel: 'Cancel',
        confirmButtonLabel: 'Discard & Switch',
      },
      maxWidth: '35%',
    });

    modalRef.onClose$.subscribe((action: string) => {
      if (action === 'ok') {
        this._doSwitchToPhoenix(routeKey, params);
      }
    });
  }

  private _doSwitchToPhoenix(
    routeKey: PlatformSwitcherRouteKey,
    params: Record<string, string | number>
  ): void {
    this._platformSwitcher.switchToPhoenix(routeKey, params).subscribe();
  }
}
